import { useCallback, useMemo } from "react";

import queryString from "query-string";
import { useLocation } from "react-router-dom";

import { ProjectViewLinkInvitationSignUp } from "../components/ProjectViewLinkInvitationSignUp/ProjectViewLinkInvitationSignUp";
import SignUp from "../components/SignUp/SignUp";
import useNavigation from "../hooks/navigation";
import { useRegister } from "../hooks/useAuth";
import { SearchParam } from "../types";
import { PageSource } from "../types/tracking";
import { searchParamAsOptionalString } from "../urlUtils";
import { isEmailInvalid } from "../utils/validationUtils";

const SignUpContainer = () => {
  const location = useLocation();
  const navigation = useNavigation();

  const appTrackingID = useMemo(() => {
    const queryParams = queryString.parse(location.search);
    return searchParamAsOptionalString(queryParams[SearchParam.TrackingID]);
  }, [location.search]);

  const {
    mutateAsync: mutateRegister,
    isPending: isRegisterPending,
    error: registerError,
  } = useRegister({ appTrackingID });

  const registerSuccessCallback = useCallback(() => {
    const isRedirecting = navigation.tryRedirectFromSearchParams(PageSource.Signup);
    if (isRedirecting) {
      return;
    }

    navigation.goToSubscription({
      additionalQueryParams: {
        [SearchParam.Source]: PageSource.Signup,
      },
    });
  }, [navigation]);

  const signUp = async (email: string, password: string, name: string, captcha?: string) => {
    try {
      await mutateRegister(
        { email, password, name, captcha },
        { onSuccess: registerSuccessCallback }
      );
    } catch (error) {
      // This is to not throw Unhandled Promise Rejection error
    }
  };

  const emailHint = useMemo(() => {
    const hashParams = new URLSearchParams(location.hash.replace("#", "?"));
    const emailHint = hashParams.get(SearchParam.EmailHint);
    if (!emailHint || isEmailInvalid(emailHint)) {
      return null;
    }

    return emailHint;
  }, [location]);

  if (emailHint) {
    return (
      <ProjectViewLinkInvitationSignUp
        submit={signUp}
        inProgress={isRegisterPending}
        signupError={registerError}
        emailHint={emailHint}
      />
    );
  }

  return <SignUp submit={signUp} inProgress={isRegisterPending} signupError={registerError} />;
};

export default SignUpContainer;
